import { PublicBookingPhoneVerificationError } from '@/services/publicBookingPhoneVerification';
import { COMMON_PHONE_CODES, PhoneUtils, type PhoneCode } from '@/lib/phone-codes';

export type PhoneVerificationFeedbackTone = 'error' | 'info' | 'success';

const DEFAULT_PHONE_COUNTRY = COMMON_PHONE_CODES.find((country) => country.iso2 === 'MX')!;

export function getDefaultPhoneCountry(): PhoneCode {
  return DEFAULT_PHONE_COUNTRY;
}

export function getEditablePhoneSeed(phone?: string | null): {
  country: PhoneCode;
  localPhone: string;
} {
  if (!phone) {
    return {
      country: DEFAULT_PHONE_COUNTRY,
      localPhone: '',
    };
  }

  const validation = PhoneUtils.validatePhoneNumber(phone);
  const country = validation.country
    ?? PhoneUtils.getCountryInfo(validation.countryCode.replace(/^\+/, ''))
    ?? DEFAULT_PHONE_COUNTRY;

  return {
    country,
    localPhone: validation.localNumber || phone.replace(/^\+/, ''),
  };
}

export function formatCountdownLabel(seconds: number): string {
  const safeSeconds = Math.max(seconds, 0);
  return `00:${safeSeconds.toString().padStart(2, '0')}`;
}

export function formatDateTimeLabel(value?: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed.toLocaleString('es-MX', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getPublicBookingPhoneVerificationErrorMessage(error: unknown): string {
  if (error instanceof PublicBookingPhoneVerificationError) {
    switch (error.code) {
      case 'public_booking_phone_verification_invalid':
        return 'Escribe un número válido con su lada para enviarte el código por WhatsApp.';
      case 'public_booking_phone_verification_already_verified':
        return 'Ese número ya está confirmado en tu cuenta. Puedes continuar con tu visita.';
      case 'public_booking_phone_verification_pending_required':
        return 'Primero confirma a qué celular quieres que enviemos el código.';
      case 'public_booking_phone_verification_challenge_not_found':
        return 'Ese código ya venció o dejó de ser válido. Te ayudamos generando uno nuevo.';
      case 'public_booking_phone_verification_retry_cooldown':
        return 'Acabamos de enviarte un código. Espera un momento antes de pedir otro.';
      case 'public_booking_phone_verification_rate_limited':
        return 'Hicimos demasiados intentos en poco tiempo. Intenta de nuevo más tarde.';
      case 'public_booking_phone_verification_delivery_target_missing':
        return 'No encontramos un número pendiente válido para el envío. Revísalo y continuamos.';
      case 'public_booking_phone_verification_delivery_failed':
        return 'No pudimos enviar el código por WhatsApp en este momento. Intenta de nuevo en unos minutos.';
      case 'tenant_access_required':
        return 'Esta confirmación solo está disponible para cuentas de inquilino activas.';
      case 'profile_not_found':
        return 'No pudimos encontrar tu perfil para terminar la confirmación. Cierra sesión y vuelve a entrar.';
      case 'internal_access_invalid':
        return 'Tu sesión ya no es válida para continuar. Actualiza la página e inicia sesión de nuevo.';
      default:
        return error.message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return 'No pudimos completar la confirmación de identidad en este momento.';
}

export function getPublicBookingPhoneVerificationFeedbackTitle(tone: PhoneVerificationFeedbackTone): string {
  switch (tone) {
    case 'success':
      return 'Todo listo';
    case 'info':
      return 'Toma nota';
    case 'error':
    default:
      return 'Necesitamos revisar esto';
  }
}